import React, { useEffect, useState } from "react";
import WizardNav from "../../components/Wizard/WizardNav";
import { useTranslation } from "react-i18next";
import LanguageTabs from "../../components/LanguageTab/LanguageTabs";
import { availableLanguages } from "../../i18n";
import { getTextForField, setTextForField } from "../../utils/formUtils";

export default function BasicData({
  stepData,
  setStepData,
  next,
  back,
  isFirst,
  canGoNext,
}) {
  const { t, i18n } = useTranslation();
  const initialLang = i18n?.language || "en";
  const [baseLang] = useState(() => initialLang);
  const [languageTabs, setLanguageTabs] = useState([]);
  const [activeLang, setActiveLang] = useState(baseLang);
  const languageOptions = availableLanguages;

  const getBaseFieldEntries = (field) => {
    const currentEntries = Array.isArray(stepData?.[field]) ? stepData[field] : [];
    if (currentEntries.length > 0) {
      return currentEntries;
    }

    if (field === "title") {
      const baseValue =
        getTextForField(stepData, "title", baseLang, baseLang) ||
        (typeof stepData?.name === "string" ? stepData.name : "");
      return [{ language: baseLang, value: baseValue }];
    }

    const baseValue =
      getTextForField(stepData, "description", baseLang, baseLang) ||
      (typeof stepData?.descriptionText === "string"
        ? stepData.descriptionText
        : typeof stepData?.description === "string"
          ? stepData.description
          : "");
    return [{ language: baseLang, value: baseValue }];
  };

  useEffect(() => {
    const usedLangs = Array.from(
      new Set(
        [baseLang, "title", "description"]
          .flatMap((field) =>
            field === baseLang
              ? [baseLang]
              : Array.isArray(stepData?.[field])
              ? stepData[field]
                  .map((entry) => entry?.language ?? entry?.lang)
                  .filter(Boolean)
              : [],
          )
          .filter(Boolean),
      ),
    );
    const nextTabs = usedLangs.length > 0 ? usedLangs : [baseLang];

    setLanguageTabs(nextTabs);
    setActiveLang((prev) => (nextTabs.includes(prev) ? prev : nextTabs[0]));
  }, [stepData, baseLang]);

  useEffect(() => {
    const nextStepData = {};

    if (!Array.isArray(stepData?.title) || stepData.title.length === 0) {
      nextStepData.title =
        typeof stepData?.name === "string" && stepData.name.trim()
          ? [{ language: baseLang, value: stepData.name }]
          : [{ language: baseLang, value: "" }];
    }

    if (
      !Array.isArray(stepData?.description) ||
      stepData.description.length === 0
    ) {
      nextStepData.description =
        typeof stepData?.descriptionText === "string" &&
        stepData.descriptionText.trim()
          ? [{ language: baseLang, value: stepData.descriptionText }]
          : typeof stepData?.description === "string" &&
              stepData.description.trim()
            ? [{ language: baseLang, value: stepData.description }]
          : [{ language: baseLang, value: "" }];
    }

    if (Object.keys(nextStepData).length > 0) {
      setStepData("step1", nextStepData);
    }
  }, [stepData, setStepData, baseLang]);

  const updateField = (field, value) => {
    const nextEntries = setTextForField(
      stepData ?? {},
      field,
      activeLang,
      value,
    )[field];

    const nextPayload = { [field]: nextEntries };
    if (field === "title" && activeLang === baseLang) {
      nextPayload.name = value;
    }
    if (field === "description" && activeLang === baseLang) {
      nextPayload.descriptionText = value;
    }

    setStepData("step1", nextPayload);
  };

  const handleAddLanguageTab = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code || languageTabs.includes(code)) return;

    const normalizedStepData = {
      ...(stepData ?? {}),
      title: getBaseFieldEntries("title"),
      description: getBaseFieldEntries("description"),
    };

    const nextTitle = setTextForField(
      normalizedStepData,
      "title",
      code,
      "",
    ).title;
    const nextDescription = setTextForField(
      { ...normalizedStepData, title: nextTitle },
      "description",
      code,
      "",
    ).description;

    setStepData("step1", {
      title: nextTitle,
      description: nextDescription,
    });
    setActiveLang(code);
  };

  const handleRemoveLanguageTab = (lang) => {
    const code = typeof lang === "string" ? lang : lang?.code;
    if (!code || languageTabs.length <= 1) return;

    const nextTitle = (stepData?.title ?? []).filter(
      (entry) => (entry?.language ?? entry?.lang) !== code,
    );
    const nextDescription = (stepData?.description ?? []).filter(
      (entry) => (entry?.language ?? entry?.lang) !== code,
    );

    if (nextTitle.length === 0 || nextDescription.length === 0) return;

    const payload = {
      title: nextTitle,
      description: nextDescription,
    };

    if (code === baseLang) {
      const nextBaseTitle = nextTitle[0]?.value ?? "";
      const nextBaseDescription = nextDescription[0]?.value ?? "";
      payload.name = nextBaseTitle;
      payload.descriptionText = nextBaseDescription;
    }

    setStepData("step1", payload);

    if (activeLang === code) {
      setActiveLang(nextTitle[0]?.language ?? baseLang);
    }
  };

  const titleValue = getTextForField(stepData, "title", activeLang, baseLang);
  const descriptionValue = getTextForField(
    stepData,
    "description",
    activeLang,
    baseLang,
  );
  const fallbackTitleValue =
    activeLang === baseLang && typeof stepData?.name === "string"
      ? stepData.name
      : titleValue;
  const fallbackDescriptionValue =
    activeLang === baseLang && typeof stepData?.description === "string"
      ? stepData.description
      : activeLang === baseLang &&
          typeof stepData?.descriptionText === "string"
        ? stepData.descriptionText
        : descriptionValue;

  return (
    <section>
      <h2 className="tittlecatalog pb4">{t("wizard.steps.basicData.title")}</h2>

      <LanguageTabs
        usedLangs={languageTabs}
        activeLang={activeLang}
        onChangeActiveLang={setActiveLang}
        onAddLang={handleAddLanguageTab}
        onRemoveLang={handleRemoveLanguageTab}
        languages={languageOptions}
        addLabel={t("common.addLanguage")}
      />

      <div className="wz-form">
        <label className="wz-label">
          <span>
            {t("wizard.steps.basicData.name")}
            <span className="required">*</span>
          </span>
          <input
            className="wz-input"
            name="title"
            value={fallbackTitleValue}
            placeholder={t("wizard.steps.basicData.name_placeholder")}
            onChange={(e) => updateField("title", e.target.value)}
          />
        </label>
        <label className="wz-label">
          {t("wizard.steps.basicData.description")}
          <textarea
            className="wz-input fontNoto"
            name="description"
            rows={4}
            value={fallbackDescriptionValue}
            placeholder={t("wizard.steps.basicData.description_placeholder")}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </label>
      </div>
      <div className="wz-footer">
        <WizardNav
          back={back}
          next={next}
          isFirst={isFirst}
          canGoNext={canGoNext}
        />
      </div>
    </section>
  );
}
